import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResumeTailoring } from '@/contexts/ResumeTailoringContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowRight, ArrowLeft, FileText, Linkedin, Mail, StickyNote, CheckCircle2, Settings } from 'lucide-react';
import { StepHeader } from './StepHeader';

const DOCUMENT_OPTIONS = [
  { id: 'resume', label: 'Resume', description: 'Primary tailored resume', icon: FileText },
  { id: 'cover-letter', label: 'Cover Letter', description: 'Personalized intro letter', icon: Mail },
  { id: 'linkedin-profile', label: 'LinkedIn Profile', description: 'LinkedIn about + headline', icon: Linkedin },
  { id: 'executive-bio', label: 'Executive Bio', description: 'Professional summary bio', icon: FileText },
];

export function TailoringPreferencesPage() {
  const navigate = useNavigate();
  const { state, dispatch } = useResumeTailoring();

  const [selectedDocs, setSelectedDocs] = useState<string[]>(
    state.selectedDocumentTypes?.length ? state.selectedDocumentTypes : ['resume']
  );
  const [additionalInstructions, setAdditionalInstructions] = useState(state.additionalInstructionsText || '');

  const toggleDoc = (id: string) => {
    setSelectedDocs(prev =>
      prev.includes(id) ? (id === 'resume' ? prev : prev.filter(d => d !== id)) : [...prev, id]
    );
  };

  const handleContinue = () => {
    dispatch({ type: 'SET_DOCUMENT_TYPES', payload: { selectedDocumentTypes: selectedDocs } });
    dispatch({
      type: 'SET_PREFERENCES',
      payload: {
        sectionsToTailor: ['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Title & headline'],
        selectedTemplateMain: state.selectedTemplateMain,
        quantificationLevel: 'moderate',
        writingStyle: 'chronological',
        selectedPrompts: {},
      },
    });
    dispatch({ type: 'SET_ADDITIONAL_INSTRUCTIONS', payload: { additionalInstructionsText: additionalInstructions } });
    navigate('/resume-tailoring-simple/step/4');
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8">
      <StepHeader currentStep={3} />

      {/* Document selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Documents to Generate
          </CardTitle>
          <CardDescription>Select which documents to create. Resume is always included.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DOCUMENT_OPTIONS.map(({ id, label, description, icon: Icon }) => {
              const selected = selectedDocs.includes(id);
              const locked = id === 'resume';
              return (
                <button
                  key={id}
                  onClick={() => !locked && toggleDoc(id)}
                  className={`text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${
                    selected
                      ? 'border-green-500 bg-green-50'
                      : 'border-border hover:border-primary/40'
                  } ${locked ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <Checkbox
                    checked={selected}
                    onCheckedChange={() => !locked && toggleDoc(id)}
                    className="mt-0.5 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                    onClick={e => e.stopPropagation()}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${selected ? 'text-green-600' : 'text-muted-foreground'}`} />
                      <Label className={`font-semibold cursor-pointer ${selected ? 'text-green-700' : ''}`}>
                        {label} {locked && <span className="text-xs font-normal text-muted-foreground">(required)</span>}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {selectedDocs.length > 1 && (
            <div className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-muted/40 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="font-medium">{selectedDocs.length} documents selected</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional instructions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-primary" />
            Special Instructions
          </CardTitle>
          <CardDescription>
            Anything specific you want the AI to focus on, emphasize, or avoid? (optional)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="e.g. Emphasize my leadership experience, avoid mentioning my gap year, highlight any Python skills, keep it under one page…"
            value={additionalInstructions}
            onChange={e => setAdditionalInstructions(e.target.value)}
            rows={5}
            className="resize-none"
          />
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => navigate('/resume-tailoring-simple/step/2')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button onClick={handleContinue} disabled={selectedDocs.length === 0} size="lg">
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
